import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '../../components/layout/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../../components/ui/tooltip';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { Checkbox } from '../../components/ui/checkbox';
import { 
  ArrowLeft, Monitor, Search, RefreshCw, LogOut, Users, UserCog, 
  Shield, AlertTriangle, Loader2, ChevronLeft, ChevronRight,
  Smartphone, Laptop, Globe
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ActiveSessionsPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [sessions, setSessions] = useState([]);
  const [stats, setStats] = useState(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(15);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [refreshing, setRefreshing] = useState(false);
  
  // Dialogs
  const [logoutSessionDialog, setLogoutSessionDialog] = useState({ open: false, session: null });
  const [logoutUserDialog, setLogoutUserDialog] = useState({ open: false, user: null, sessions: [] });
  const [emergencyLogoutDialog, setEmergencyLogoutDialog] = useState({ open: false });
  const [includeAdmins, setIncludeAdmins] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  const fetchSessions = useCallback(async () => {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        page_size: pageSize.toString()
      });
      if (search) params.append('search', search);
      if (roleFilter && roleFilter !== 'all') params.append('role_filter', roleFilter);
      
      const response = await axios.get(`${API}/admin/sessions?${params}`);
      setSessions(response.data.sessions);
      setTotal(response.data.total);
    } catch (error) {
      console.error('Failed to fetch sessions:', error);
      toast.error('Failed to load active sessions');
    }
  }, [page, pageSize, search, roleFilter]);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/sessions/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const loadData = useCallback(async () => {
    setLoading(true);
    await Promise.all([fetchSessions(), fetchStats()]);
    setLoading(false);
  }, [fetchSessions]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await Promise.all([fetchSessions(), fetchStats()]);
    setRefreshing(false);
    toast.success('Sessions refreshed');
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchSessions();
  };

  const handleLogoutSession = async () => {
    if (!logoutSessionDialog.session) return;
    
    setActionLoading(true);
    try {
      await axios.delete(`${API}/admin/sessions/${logoutSessionDialog.session.session_id}`);
      toast.success(`Session terminated for ${logoutSessionDialog.session.user_name}`);
      setLogoutSessionDialog({ open: false, session: null });
      await Promise.all([fetchSessions(), fetchStats()]);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to terminate session');
    } finally {
      setActionLoading(false);
    }
  };

  const handleLogoutUserSessions = async () => {
    if (!logoutUserDialog.user) return;
    
    setActionLoading(true);
    try {
      const response = await axios.delete(`${API}/admin/sessions/user/${logoutUserDialog.user.user_id}`);
      toast.success(response.data.message);
      setLogoutUserDialog({ open: false, user: null, sessions: [] });
      await Promise.all([fetchSessions(), fetchStats()]);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to terminate sessions');
    } finally {
      setActionLoading(false);
    }
  };

  const handleEmergencyLogout = async () => {
    setActionLoading(true);
    try {
      const response = await axios.delete(`${API}/admin/sessions/all/emergency?include_admins=${includeAdmins}`);
      toast.success(response.data.message);
      setEmergencyLogoutDialog({ open: false });
      setIncludeAdmins(false);
      await Promise.all([fetchSessions(), fetchStats()]);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Emergency logout failed');
    } finally {
      setActionLoading(false);
    }
  };

  const openLogoutUserDialog = (session) => {
    // Find all sessions for this user
    const userSessions = sessions.filter(s => s.user_id === session.user_id);
    setLogoutUserDialog({
      open: true,
      user: session,
      sessions: userSessions
    });
  };

  const getDeviceIcon = (deviceName) => {
    const name = (deviceName || '').toLowerCase();
    if (name.includes('mobile') || name.includes('iphone') || name.includes('android')) {
      return <Smartphone className="w-4 h-4" />;
    }
    if (name.includes('windows') || name.includes('mac') || name.includes('linux')) {
      return <Laptop className="w-4 h-4" />;
    }
    return <Globe className="w-4 h-4" />;
  };

  const getRoleBadge = (role) => {
    switch (role) {
      case 'admin':
        return <Badge className="bg-red-100 text-red-700 border-red-200">Admin</Badge>;
      case 'sub_admin':
        return <Badge className="bg-orange-100 text-orange-700 border-orange-200">Sub-Admin</Badge>;
      default:
        return <Badge className="bg-blue-100 text-blue-700 border-blue-200">User</Badge>;
    }
  };

  const formatTime = (isoString) => {
    if (!isoString) return 'N/A';
    const date = new Date(isoString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const getTimeAgo = (isoString) => {
    if (!isoString) return '';
    const date = new Date(isoString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays}d ago`;
  };

  const totalPages = Math.ceil(total / pageSize);

  if (loading) {
    return (
      <Layout>
        <div className="flex justify-center items-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-[#6366F1]" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/admin/tools')}
              className="shrink-0"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                <Monitor className="w-6 h-6 text-purple-600" />
                Active Sessions
              </h1>
              <p className="text-gray-600">Monitor and manage user sessions</p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleRefresh}
              disabled={refreshing}
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button
              variant="destructive"
              size="sm"
              onClick={() => setEmergencyLogoutDialog({ open: true })}
              className="bg-red-600 hover:bg-red-700"
            >
              <AlertTriangle className="w-4 h-4 mr-2" />
              Emergency Logout All
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Total Active</p>
                    <p className="text-2xl font-bold">{stats.total_active}</p>
                  </div>
                  <Monitor className="w-8 h-8 text-purple-500 opacity-50" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Admins</p>
                    <p className="text-2xl font-bold">{stats.by_role?.admin || 0}</p>
                  </div>
                  <Shield className="w-8 h-8 text-red-500 opacity-50" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Sub-Admins</p>
                    <p className="text-2xl font-bold">{stats.by_role?.sub_admin || 0}</p>
                  </div>
                  <UserCog className="w-8 h-8 text-orange-500 opacity-50" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Users</p>
                    <p className="text-2xl font-bold">{stats.by_role?.user || 0}</p>
                  </div>
                  <Users className="w-8 h-8 text-blue-500 opacity-50" />
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="pt-4">
            <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Search by name or INDoS..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Select value={roleFilter} onValueChange={(v) => { setRoleFilter(v); setPage(1); }}>
                <SelectTrigger className="w-full sm:w-[180px]">
                  <SelectValue placeholder="Filter by role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Roles</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="sub_admin">Sub-Admin</SelectItem>
                  <SelectItem value="user">User</SelectItem>
                </SelectContent>
              </Select>
              <Button type="submit" variant="secondary">
                <Search className="w-4 h-4 mr-2" />
                Search
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Sessions Table */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Active Sessions ({total})</CardTitle>
            <CardDescription>
              Currently logged-in users across all devices
            </CardDescription>
          </CardHeader>
          <CardContent>
            {sessions.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Monitor className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>No active sessions found</p>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>User</TableHead>
                        <TableHead>Role</TableHead>
                        <TableHead>Device</TableHead>
                        <TableHead>IP Address</TableHead>
                        <TableHead>Last Active</TableHead>
                        <TableHead>Login Time</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {sessions.map((session) => (
                        <TableRow key={session.session_id}>
                          <TableCell>
                            <div>
                              <p className="font-medium">{session.user_name}</p>
                              <p className="text-sm text-gray-500">{session.user_indos}</p>
                            </div>
                          </TableCell>
                          <TableCell>{getRoleBadge(session.user_role)}</TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              {getDeviceIcon(session.device_name)}
                              <span className="text-sm truncate max-w-[150px]" title={session.device_name}>
                                {session.device_name}
                              </span>
                            </div>
                          </TableCell>
                          <TableCell>
                            <span className="text-sm font-mono">{session.ip_address || 'N/A'}</span>
                          </TableCell>
                          <TableCell>
                            <div>
                              <p className="text-sm">{getTimeAgo(session.last_active_at)}</p>
                              <p className="text-xs text-gray-400">{formatTime(session.last_active_at)}</p>
                            </div>
                          </TableCell>
                          <TableCell>
                            <p className="text-sm">{formatTime(session.created_at)}</p>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-2">
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => setLogoutSessionDialog({ open: true, session })}
                                      className="text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                                    >
                                      <LogOut className="w-4 h-4" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p>Terminate this session</p>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => openLogoutUserDialog(session)}
                                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                    >
                                      <Users className="w-4 h-4" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p>Terminate all sessions for this user</p>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-between mt-4 pt-4 border-t">
                    <p className="text-sm text-gray-500">
                      Showing {((page - 1) * pageSize) + 1} to {Math.min(page * pageSize, total)} of {total} sessions
                    </p>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPage(p => Math.max(1, p - 1))}
                        disabled={page === 1}
                      >
                        <ChevronLeft className="w-4 h-4" />
                      </Button>
                      <span className="text-sm px-2">
                        Page {page} of {totalPages}
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                        disabled={page === totalPages}
                      >
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>

        {/* Logout Single Session Dialog */}
        <AlertDialog open={logoutSessionDialog.open} onOpenChange={(open) => !open && setLogoutSessionDialog({ open: false, session: null })}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Terminate Session</AlertDialogTitle>
              <AlertDialogDescription asChild>
                <div>
                  <span>Are you sure you want to terminate this session?</span>
                  {logoutSessionDialog.session && (
                    <div className="mt-3 p-3 bg-gray-50 rounded-lg text-sm">
                      <div><strong>User:</strong> {logoutSessionDialog.session.user_name}</div>
                      <div><strong>Device:</strong> {logoutSessionDialog.session.device_name}</div>
                      <div><strong>IP:</strong> {logoutSessionDialog.session.ip_address || 'N/A'}</div>
                    </div>
                  )}
                  <div className="mt-3 text-orange-600">
                    The user will be logged out immediately from this device.
                  </div>
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={actionLoading}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleLogoutSession}
                disabled={actionLoading}
                className="bg-orange-600 hover:bg-orange-700"
              >
                {actionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <LogOut className="w-4 h-4 mr-2" />}
                Terminate Session
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Logout All User Sessions Dialog */}
        <AlertDialog open={logoutUserDialog.open} onOpenChange={(open) => !open && setLogoutUserDialog({ open: false, user: null, sessions: [] })}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Terminate All User Sessions</AlertDialogTitle>
              <AlertDialogDescription asChild>
                <div>
                  <span>Are you sure you want to terminate ALL sessions for this user?</span>
                  {logoutUserDialog.user && (
                    <div className="mt-3 p-3 bg-gray-50 rounded-lg text-sm">
                      <div><strong>User:</strong> {logoutUserDialog.user.user_name}</div>
                      <div><strong>INDoS:</strong> {logoutUserDialog.user.user_indos}</div>
                      <div><strong>Active Sessions:</strong> {logoutUserDialog.sessions.length}</div>
                    </div>
                  )}
                  <div className="mt-3 text-red-600">
                    The user will be logged out from ALL devices immediately.
                  </div>
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={actionLoading}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleLogoutUserSessions}
                disabled={actionLoading}
                className="bg-red-600 hover:bg-red-700"
              >
                {actionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Users className="w-4 h-4 mr-2" />}
                Terminate All Sessions
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Emergency Logout All Dialog */}
        <AlertDialog open={emergencyLogoutDialog.open} onOpenChange={(open) => { if (!open) { setEmergencyLogoutDialog({ open: false }); setIncludeAdmins(false); } }}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2 text-red-600">
                <AlertTriangle className="w-5 h-5" />
                Emergency: Logout All Users
              </AlertDialogTitle>
              <AlertDialogDescription asChild>
                <div>
                  <div className="text-red-600 font-medium mb-3">
                    This will immediately terminate ALL active sessions!
                  </div>
                  {stats && (
                    <div className="p-3 bg-red-50 rounded-lg border border-red-200 mb-4 text-sm">
                      <strong>Sessions to terminate:</strong> {includeAdmins ? stats.total_active : (stats.by_role?.user || 0)} {!includeAdmins && '(excluding admins)'}
                    </div>
                  )}
                  <div className="flex items-center space-x-2 p-3 bg-gray-50 rounded-lg">
                    <Checkbox
                      id="include-admins"
                      checked={includeAdmins}
                      onCheckedChange={setIncludeAdmins}
                    />
                    <label
                      htmlFor="include-admins"
                      className="text-sm font-medium leading-none cursor-pointer"
                    >
                      Include admin sessions (you will be logged out too)
                    </label>
                  </div>
                  <div className="mt-4 text-sm text-gray-500">
                    Use this only for emergencies like security breaches or critical deployments.
                  </div>
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={actionLoading}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleEmergencyLogout}
                disabled={actionLoading}
                className="bg-red-600 hover:bg-red-700"
              >
                {actionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <AlertTriangle className="w-4 h-4 mr-2" />}
                Confirm Emergency Logout
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Layout>
  );
};

export default ActiveSessionsPage;
